import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Bell, Plus, FilePdf, X, MagnifyingGlass, Buildings, Scales, ArrowsClockwise, Warning, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import SignatarioDownloadModal from "@/components/SignatarioDownloadModal";

const TIPOS = [
  { v: "prorrogacao", l: "Prorrogação Contratual (Art. 132)" },
  { v: "prorrogacao_servicos_continuados", l: "Prorrogação — Serviços/Compras continuadas (Art. 107)" },
  { v: "prorrogacao_obras_engenharia", l: "Prorrogação — Obras/Engenharia (Art. 111)" },
  { v: "aditivo_valor", l: "Aditivo de Valor (Art. 124-125)" },
  { v: "inadimplencia", l: "Inadimplência Contratual (Art. 137)" },
  { v: "sancao", l: "Aplicação de Sanção (Art. 156-158)" },
  { v: "convocacao", l: "Convocação para Assinatura (Art. 90)" },
];

const tipoLabel = (v) => TIPOS.find((t) => t.v === v)?.l || v;

export default function Notificacoes() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState("");
  const [showModal, setShowModal] = useState(false);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (tipo) params.tipo = tipo;
    api.get("/notificacoes", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); });
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search, tipo]);

  const downloadPdf = async (n) => {
    try {
      const res = await fetch(`${API}/notificacoes/${n.id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `notificacao_${n.id.slice(0, 8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Notificação gerada");
    } catch { toast.error("Erro ao gerar PDF"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">Lei 14.133/2021</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Notificações às Empresas</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
            Emissão de notificações formais às empresas contratadas, com base legal explícita
            na nova Lei de Licitações (prorrogação, aditivo, inadimplência, sanção, convocação).
          </p>
        </div>
        <button data-testid="new-notif" onClick={() => setShowModal(true)}
                className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium flex items-center gap-2">
          <Plus size={16} weight="bold" /> Nova notificação
        </button>
      </div>

      <RenovacoesAnualPanel />

      <div className="tce-card p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input data-testid="notif-search" value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   placeholder="Buscar por contrato, fornecedor ou assunto…"
                   className="tce-input w-full pl-10 pr-4 py-2.5 rounded-md text-sm" />
          </div>
          <select data-testid="notif-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
                  className="tce-input px-3 py-2.5 rounded-md text-sm">
            <option value="">Todos os tipos</option>
            {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
        <div className="text-xs text-[#94A3B8] mt-3">{total} notificação(ões)</div>
      </div>

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="tce-card p-10 text-center">
            <Bell size={36} weight="duotone" className="text-[#F59E0B] mx-auto mb-3" />
            <div className="font-heading text-lg">Nenhuma notificação emitida</div>
          </div>
        ) : items.map((n, i) => (
          <div key={n.id} data-testid={`notif-row-${i}`} className="tce-card p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-md bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B] flex-shrink-0">
                <Scales size={18} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#F59E0B]">{tipoLabel(n.tipo)}</span>
                  <span className="text-xs text-[#94A3B8]">·</span>
                  <Link to={`/contratos/${n.contract_id}`} className="font-mono text-[#F59E0B] hover:text-[#FCD34D] text-sm">{n.contract_numero}</Link>
                  {n.aditivo_origem_numero && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#FCD34D] border border-[#F59E0B]/30"
                          title={`Vinculada ao Aditivo ${n.aditivo_origem_numero}`}>
                      ← ADT {n.aditivo_origem_numero}
                    </span>
                  )}
                  {n.renovacao_saldo_id && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#009C3B]/15 text-[#12B850] border border-[#009C3B]/30"
                          title="Renovação de Saldo gerada em conjunto">
                      REN auto
                    </span>
                  )}
                  {n.auto_generated && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#002776]/20 text-[#3B82F6]"
                          title="Emitida automaticamente pelo sistema">
                      auto
                    </span>
                  )}
                </div>
                <div className="font-heading text-lg text-[#E9EEF5]">{n.assunto}</div>
                <div className="text-sm text-[#94A3B8] mt-1.5 line-clamp-2">{n.conteudo}</div>
                <div className="flex items-center gap-4 mt-3 text-xs text-[#94A3B8]">
                  <span className="flex items-center gap-1.5">
                    <Buildings size={12} weight="duotone" className="text-[#F59E0B]" />
                    {n.municipio_nome}
                  </span>
                  <span>Empresa: <strong className="text-[#E9EEF5]">{n.fornecedor}</strong></span>
                  <span>CNPJ: {n.cnpj_fornecedor || "—"}</span>
                  <span>Prazo: {n.prazo_resposta_dias} dias</span>
                  <span>Emitida em {new Date(n.created_at).toLocaleString("pt-BR")}</span>
                </div>
                <div className="text-xs text-[#FCD34D] mt-2 italic">📜 {n.base_legal}</div>
              </div>
              <button data-testid={`notif-pdf-${i}`} onClick={() => downloadPdf(n)}
                      className="px-4 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] text-sm font-medium flex items-center gap-2 flex-shrink-0">
                <FilePdf size={14} weight="duotone" /> Gerar PDF
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && <NovaNotifModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

function NovaNotifModal({ onClose, onSaved }) {
  const [contracts, setContracts] = useState([]);
  const [form, setForm] = useState({
    contract_id: "", tipo: "prorrogacao", assunto: "", conteudo: "", prazo_resposta_dias: 5,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/contracts?limit=200").then((r) => setContracts(r.data.items));
  }, []);

  const fillTemplate = (tipo) => {
    const templates = {
      prorrogacao: {
        assunto: "Comunicação de Prorrogação Contratual",
        conteudo: "Comunicamos a V.Sa. que, com fundamento no Art. 132 da Lei 14.133/2021, fica formalizada a prorrogação da vigência contratual, mantendo-se as demais condições originalmente pactuadas. Solicitamos manifestação no prazo legal acerca da assinatura do termo aditivo correspondente.",
      },
      prorrogacao_servicos_continuados: {
        assunto: "Prorrogação de Contrato — Serviços e Compras de Natureza Continuada",
        conteudo: "Comunicamos a V.Sa. que, com fundamento no Art. 107 da Lei 14.133/2021, este órgão promoverá a prorrogação da vigência contratual de serviços e/ou compras de natureza continuada, demonstrada a vantajosidade da contratação para a Administração Pública. Nos termos do referido dispositivo, a vigência poderá atingir o limite total de 10 (dez) anos. Solicitamos manifestação formal acerca da renovação no prazo legal, bem como apresentação dos documentos de habilitação atualizados.",
      },
      prorrogacao_obras_engenharia: {
        assunto: "Prorrogação de Prazo — Obras e Serviços de Engenharia",
        conteudo: "Comunicamos a V.Sa. que, com fundamento no Art. 111 da Lei 14.133/2021, este órgão formalizará a prorrogação do prazo de execução das obras e/ou serviços de engenharia objeto do contrato em referência, observado o cronograma físico-financeiro reprogramado. Mantêm-se inalteradas as condições econômico-financeiras pactuadas. Solicitamos manifestação acerca do termo aditivo no prazo de 5 (cinco) dias úteis.",
      },
      aditivo_valor: {
        assunto: "Aditivo de Valor Contratual",
        conteudo: "Comunicamos a V.Sa. que, observados os limites do Art. 125 da Lei 14.133/2021, será celebrado aditivo de valor ao contrato em referência. Solicitamos a manifestação formal acerca dos termos propostos no prazo legal.",
      },
      inadimplencia: {
        assunto: "Notificação de Inadimplência Contratual",
        conteudo: "Considerando o descumprimento das obrigações pactuadas, notificamos V.Sa. para que, no prazo de 5 (cinco) dias úteis, apresente justificativa formal e plano de regularização, sob pena de aplicação das sanções previstas no Art. 156 da Lei 14.133/2021.",
      },
      sancao: {
        assunto: "Notificação Prévia para Aplicação de Sanção",
        conteudo: "Nos termos do Art. 158 da Lei 14.133/2021, fica V.Sa. notificado(a) da abertura de processo administrativo para apuração de descumprimento contratual, sendo assegurado o direito ao contraditório e à ampla defesa no prazo de 15 (quinze) dias úteis.",
      },
      convocacao: {
        assunto: "Convocação para Assinatura de Contrato",
        conteudo: "Com fulcro no Art. 90 da Lei 14.133/2021, fica V.Sa. CONVOCADO(A) a comparecer ao órgão contratante no prazo de 5 (cinco) dias úteis para assinatura do termo contratual, sob pena de decadência do direito à contratação.",
      },
    };
    const t = templates[tipo];
    if (t) setForm((f) => ({ ...f, tipo, assunto: t.assunto, conteudo: t.conteudo }));
    else setForm((f) => ({ ...f, tipo }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contract_id) { toast.error("Selecione um contrato"); return; }
    setSaving(true);
    try {
      await api.post("/notificacoes", form);
      toast.success("Notificação emitida");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao emitir");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div className="flex items-center gap-2">
            <Bell size={20} weight="duotone" className="text-[#F59E0B]" />
            <div className="font-heading text-xl">Nova notificação à empresa</div>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#F59E0B]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="uppercase-label block mb-2">Contrato</label>
            <select data-testid="notif-form-contract" required value={form.contract_id}
                    onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                    className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
              <option value="">Selecione…</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{c.numero} — {c.fornecedor?.slice(0, 40)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="uppercase-label block mb-2">Tipo de notificação</label>
            <select data-testid="notif-form-tipo" value={form.tipo}
                    onChange={(e) => fillTemplate(e.target.value)}
                    className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
              {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <p className="text-xs text-[#94A3B8] mt-1.5">
              O assunto e conteúdo são pré-preenchidos com modelo da Lei 14.133. Edite conforme necessário.
            </p>
          </div>
          <div>
            <label className="uppercase-label block mb-2">Assunto</label>
            <input data-testid="notif-form-assunto" required value={form.assunto}
                   onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                   className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
          </div>
          <div>
            <label className="uppercase-label block mb-2">Conteúdo da notificação</label>
            <textarea data-testid="notif-form-conteudo" required rows={6} value={form.conteudo}
                      onChange={(e) => setForm({ ...form, conteudo: e.target.value })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
          </div>
          <div>
            <label className="uppercase-label block mb-2">Prazo para resposta (dias úteis)</label>
            <input data-testid="notif-form-prazo" type="number" min={1} max={30}
                   value={form.prazo_resposta_dias}
                   onChange={(e) => setForm({ ...form, prazo_resposta_dias: parseInt(e.target.value) || 5 })}
                   className="tce-input w-32 px-3 py-2.5 rounded-md text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">
              Cancelar
            </button>
            <button data-testid="notif-form-save" disabled={saving}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50">
              {saving ? "Emitindo…" : "Emitir notificação"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RenovacoesAnualPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(null);
  const [pendingDownload, setPendingDownload] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/renovacoes-pendentes", { params: { dias: 60 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const decidir = async (cid, numero, renovar, municipioId) => {
    const msg = renovar
      ? `Confirma a RENOVAÇÃO anual do contrato ${numero}?\n\nO saldo do período será reiniciado (valor_executado → R$ 0,00) e a próxima decisão será agendada para daqui a 12 meses.`
      : `Confirma o ENCERRAMENTO do contrato ${numero}?\n\nO status será alterado para 'encerrado_nao_renovado'.`;
    const justificativa = window.prompt(msg + "\n\nInforme a justificativa:");
    if (justificativa === null) return;
    setProcessando(cid);
    try {
      const { data } = await api.post(`/contracts/${cid}/renovar-saldo`, { renovar, justificativa });
      toast.success((data.mensagem || "Decisão registrada") + " · Abrindo Editor Inteligente para revisão…");
      // Redireciona para o Editor Inteligente onde o gestor revisa e gera PDF
      if (data.snapshot_id) {
        navigate(`/editor/renovacao/${data.snapshot_id}`);
        return;
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar decisão");
    } finally { setProcessando(null); }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div data-testid="renovacoes-panel" className="tce-card p-5 border-l-4 border-l-[#1E3A8A]">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#1E3A8A]/15 flex items-center justify-center flex-shrink-0">
          <ArrowsClockwise size={20} weight="duotone" className="text-[#3B82F6]" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading text-lg">Renovação anual pendente</div>
            <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-[#1E3A8A]/20 text-[#3B82F6] tracking-wide">
              Art. 107 — Lei 14.133/2021
            </span>
          </div>
          <p className="text-xs text-[#94A3B8] mt-1">
            {items.length} contrato(s) de serviços/compras continuadas próximo(s) da data de aniversário.
            Decida se deseja renovar (saldo reinicia) ou encerrar.
          </p>
          <div className="mt-4 space-y-2">
            {items.map((c) => {
              const dias = c.dias_para_renovacao;
              const urgente = dias <= 15;
              return (
                <div key={c.id} data-testid={`renov-${c.id}`} className="bg-[#0B1220] border border-[#334155] rounded-md p-3">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/contratos/${c.id}`} className="font-mono text-[#F59E0B] hover:text-[#FCD34D] text-sm">
                          {c.numero}
                        </Link>
                        <span className="text-xs text-[#94A3B8]">·</span>
                        <span className="text-xs text-[#94A3B8] flex items-center gap-1">
                          <Buildings size={12} /> {c.municipio_nome}
                        </span>
                        {urgente && (
                          <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#EF4444]/15 text-[#EF4444] flex items-center gap-1">
                            <Warning size={10} /> Urgente
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#E9EEF5] mt-1 truncate">{(c.objeto || "").slice(0, 100)}</div>
                      <div className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-3 flex-wrap">
                        <span>Aniversário: <strong className="text-[#FCD34D]">{c.data_proxima_renovacao}</strong> ({dias >= 0 ? `em ${dias} dias` : `${Math.abs(dias)} dias atrás`})</span>
                        <span>Saldo: <strong className="text-[#10B981]">R$ {(c.saldo || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong></span>
                        <span>({(c.saldo_pct || 0).toFixed(1)}% disponível)</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        data-testid={`renov-sim-${c.id}`}
                        onClick={() => decidir(c.id, c.numero, true, c.municipio_id)}
                        disabled={processando === c.id}
                        className="px-3 py-1.5 rounded-md bg-[#10B981] text-white hover:bg-[#10B981]/80 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <CheckCircle size={12} weight="duotone" /> Renovar
                      </button>
                      <button
                        data-testid={`renov-nao-${c.id}`}
                        onClick={() => decidir(c.id, c.numero, false, c.municipio_id)}
                        disabled={processando === c.id}
                        className="px-3 py-1.5 rounded-md border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 text-xs font-medium disabled:opacity-50"
                      >
                        Não renovar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {pendingDownload && (
        <SignatarioDownloadModal
          municipioId={pendingDownload.municipio_id}
          urlBase={`/renovacoes/${pendingDownload.snapshot_id}/pdf`}
          filename={`termo_renovacao_${(pendingDownload.numero || "contrato").replace(/[^\w.-]/g, "_")}_${pendingDownload.renovado ? "RENOVADO" : "NAO_RENOVADO"}.pdf`}
          title="Termo de Renovação Anual"
          subtitle={`Contrato ${pendingDownload.numero || ""} — Art. 107 Lei 14.133/2021`}
          onClose={() => setPendingDownload(null)}
        />
      )}
    </div>
  );
}
